/** Grid auto-refresh — the state behind the toolbar's Refresh control.
 *
 *  Polling itself is TanStack's `refetchInterval`; this hook owns the chosen
 *  period and the rules around it:
 *
 *  - **Audited before it starts.** Picking a period POSTs `/grid_autorefresh/`
 *    (one `grid.autorefresh.enabled` audit row) and the period takes effect only
 *    once that succeeds — a failed call leaves the grid as it was, so polling
 *    never runs unrecorded. Off is immediate and not audited. A page default
 *    (`defaultIntervalMs`, Background Tasks) is not a user choice and is not
 *    audited either.
 *  - **Never persisted.** Component state only: Off on every page load, so a
 *    forgotten tab can't keep polling a heavy grid across visits.
 *  - **Pauses** while the query is in error (a `statement_timeout` must not be
 *    re-run every tick) and while the caller says so (`queryOptions(true)` — the
 *    extended-timeout tier, which writes its own audit row per request). Hidden
 *    tabs never poll (TanStack's `refetchIntervalInBackground` default).
 */
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { recordEnabled } from '@/api/gridAutorefresh';
import { useSnackbar } from '@/components/Snackbar';

import type { GridRefreshToolbarState } from './GridRefreshControl';

/** The periods the toolbar offers. Mirrors the backend's
 *  `AUTOREFRESH_PERIODS_SECONDS` (apiv2/serializers/console/grid_autorefresh.py),
 *  which rejects anything else. */
export const AUTO_REFRESH_PERIODS_MS = [10_000, 15_000, 30_000, 45_000, 60_000] as const;

export interface GridAutoRefresh {
  /** 0 = off. */
  intervalMs: number;
  /** When the current period took effect (epoch ms; 0 while off). TanStack
   *  re-arms its interval timer when the period changes, so this is one of
   *  the two countdown anchors (see `gridRefreshToolbar`). */
  enabledAt: number;
  /** The audit call for a pick is in flight. */
  pending: boolean;
  /** User pick. A non-zero period is audited first; see module docs. */
  setIntervalMs: (ms: number) => void;
  /** Spread into the grid's `useQuery`. `paused` suspends polling (the
   *  extended-timeout tier). */
  queryOptions: (paused?: boolean) => {
    refetchInterval: (query: { state: { status: string } }) => number | false;
    refetchOnWindowFocus: boolean;
  };
}

interface Options {
  /** The grid's codename (its preset grid key) — recorded in the audit row. */
  grid: string;
  /** Page default period; NOT audited (the user didn't choose it). */
  defaultIntervalMs?: number;
}

export function useGridAutoRefresh({ grid, defaultIntervalMs = 0 }: Options): GridAutoRefresh {
  const snackbar = useSnackbar();
  const [state, setState] = useState(() => ({
    intervalMs: defaultIntervalMs,
    enabledAt: defaultIntervalMs > 0 ? Date.now() : 0,
  }));
  const audit = useMutation({ mutationFn: recordEnabled });

  const setIntervalMs = (ms: number) => {
    if (ms === state.intervalMs || audit.isPending) return;
    if (ms === 0) {
      setState({ intervalMs: 0, enabledAt: 0 });
      return;
    }
    audit.mutate(
      {
        grid,
        interval_seconds: ms / 1000,
        previous_interval_seconds: state.intervalMs / 1000,
        path: window.location.pathname,
      },
      {
        onSuccess: () => setState({ intervalMs: ms, enabledAt: Date.now() }),
        onError: () =>
          snackbar.show(
            'Auto-refresh not enabled: the audit log could not record it. Try again.',
            'error',
          ),
      },
    );
  };

  const queryOptions = (paused = false) => {
    const active = state.intervalMs > 0 && !paused;
    return {
      refetchInterval: (query: { state: { status: string } }) =>
        active && query.state.status !== 'error' ? state.intervalMs : false,
      // The console default is off; while polling, coming back to the tab
      // refreshes a stale page at once instead of waiting out the interval.
      refetchOnWindowFocus: active,
    };
  };

  return {
    intervalMs: state.intervalMs,
    enabledAt: state.enabledAt,
    pending: audit.isPending,
    setIntervalMs,
    queryOptions,
  };
}

const SLUG = /^[A-Za-z0-9_-]{1,40}$/;

/** A grid codename for grids with no preset/count-mode key: the leading
 *  slug-like string parts of the query key (`['preflight', 'templates']` →
 *  `preflight.templates`; `queryKey[0]` alone would collide). Stops at the
 *  first non-slug part, so free text or ids in the key never reach the audit
 *  row — and the result always passes the endpoint's codename validation. */
export function gridIdFromQueryKey(queryKey: readonly unknown[]): string {
  const parts: string[] = [];
  for (const part of queryKey) {
    if (typeof part !== 'string' || !SLUG.test(part) || parts.length === 3) break;
    parts.push(part);
  }
  return parts.join('.') || 'grid';
}

/** The subset of a `useQuery` result the toolbar control reads. */
export interface RefreshableQuery {
  refetch: (options?: { cancelRefetch?: boolean }) => Promise<unknown>;
  isFetching: boolean;
  isError: boolean;
  dataUpdatedAt: number;
  errorUpdatedAt: number;
}

/** Build the toolbar's `refresh` config for a grid's list query.
 *
 *  `onRefresh` defaults to `refetch({ cancelRefetch: false })`: a click during
 *  a poll joins the in-flight request instead of dropping it — the list calls
 *  pass no abort signal, so a dropped request keeps running on the server and
 *  the click would double the database work.
 *
 *  Countdown anchor: TanStack re-arms its `setInterval` on every query state
 *  update (query-core `queryObserver.ts` `onQueryUpdate` → `#updateTimers`)
 *  and when the period changes, so the next tick is due one period after the
 *  later of the last settle and `enabledAt`. Pinned by
 *  `ResourceDataGrid.refresh.test.tsx` — a TanStack change that breaks it
 *  fails there instead of silently skewing the countdown. */
export function gridRefreshToolbar(
  query: RefreshableQuery,
  autoRefresh: GridAutoRefresh | undefined,
  opts: {
    /** The page's own refresh (e.g. Find & Replace's forced re-run). */
    onRefresh?: () => void;
    /** The query is disabled — `refetch()` ignores `enabled: false`. */
    disabled?: boolean;
    tooltip?: string;
    /** Same flag the page passed to `queryOptions` (extended-timeout tier). */
    paused?: boolean;
  } = {},
): GridRefreshToolbarState {
  return {
    onRefresh: opts.onRefresh ?? (() => void query.refetch({ cancelRefetch: false })),
    refreshing: query.isFetching,
    ...(opts.disabled ? { disabled: true } : {}),
    ...(opts.tooltip ? { tooltip: opts.tooltip } : {}),
    ...(autoRefresh
      ? {
          auto: {
            intervalMs: autoRefresh.intervalMs,
            pending: autoRefresh.pending,
            setIntervalMs: autoRefresh.setIntervalMs,
            pausedReason: opts.paused ? 'extended' : query.isError ? 'error' : null,
            anchorMs: Math.max(query.dataUpdatedAt, query.errorUpdatedAt, autoRefresh.enabledAt),
          },
        }
      : {}),
  };
}
